import React from "react";
import { Payload, ValueType } from "recharts/types/component/DefaultTooltipContent";
import {
  BpmDateString,
  CompanyFiscalCal,
  lessThanIgnoreTime
} from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import { OutcomeVariableType } from "@blisspointmedia/bpm-types/dist/Forecasting";
import ChartTooltipV2, {
  KeyShapeOptions,
  PayloadItem
} from "../../Components/Charts/ChartTooltip/ChartTooltipV2";
import { headerFormatter } from "../../Components/Charts/ChartUtils";
import { Brand100, Brand25, Brand50, Brand70, Neutral400 } from "../../utils/colors";
import { parseEpochDate } from "../../utils/date-utils";
import {
  ForecastChartViewLabels,
  ForecastKpiViewOption,
  formatCpm,
  formatRawBudget,
  formatSpend,
  HISTORICAL_KEY,
  KPI_CI_VIEW_SUFFICES,
  KPI_VIEW_SUFFICES
} from "../forecastingConstants";
import { getCiFormatter, getKpiFormatter } from "../forecastingUtils";

type TooltipInput = {
  timestamp: number;
  payload: Payload<ValueType, string | number>[] | undefined;
};
type TooltipRenderer = (input: TooltipInput) => React.ReactNode;

interface PrimaryOnlyTooltipBuilderInput {
  earliestPredictedDate: BpmDateString;
  dateGrouping: string;
  fiscalCal: CompanyFiscalCal;
  primaryBudget: number;
  outcomeVar: OutcomeVariableType;
  kpiView: ForecastKpiViewOption;
}

export const buildKpiPrimaryOnlyTooltipRenderer = ({
  earliestPredictedDate,
  dateGrouping,
  fiscalCal,
  primaryBudget,
  outcomeVar,
  kpiView,
}: PrimaryOnlyTooltipBuilderInput): TooltipRenderer => {
  return ({ timestamp, payload }: TooltipInput) => {
    if (!payload || payload.length === 0) {
      return null;
    }

    const dateString = parseEpochDate(timestamp);
    const formattedLabel = headerFormatter(dateGrouping, dateString, fiscalCal);
    const dataPayload = payload[0].payload;
    const kpiViewSuffix = kpiView === ForecastKpiViewOption.TOTAL ? "kpi" : "kpiEfficiency";
    const kpiCIViewSuffix =
      kpiView === ForecastKpiViewOption.TOTAL
        ? "confidenceInterval"
        : "efficiencyConfidenceInterval";
    const label = ForecastChartViewLabels.Volume[kpiView];
    const formatKpi = getKpiFormatter(outcomeVar, kpiView);
    const formatCi = getCiFormatter(outcomeVar, kpiView);

    const historicalRenderer = () => {
      const kpiValue = dataPayload[`${HISTORICAL_KEY}-${kpiViewSuffix}`];

      const items: PayloadItem[] = [
        {
          label,
          value: formatKpi(kpiValue),
          color: Brand25,
          keyShape: KeyShapeOptions.LINE,
        },
      ];
      return <ChartTooltipV2 headers={[formattedLabel]} items={items} />;
    };

    const forecastedRenderer = () => {
      const kpiValue = dataPayload[`${primaryBudget}-${kpiViewSuffix}`];
      const ciValue = dataPayload[`${primaryBudget}-${kpiCIViewSuffix}`];

      const items: PayloadItem[] = [
        {
          label,
          value: formatKpi(kpiValue),
          color: Brand70,
          keyShape: KeyShapeOptions.DASHED_LINE,
        },
        {
          label: `${label} Range`,
          value: formatCi(ciValue),
          color: Brand25,
          keyShape: KeyShapeOptions.SQUARE,
        },
      ];

      return <ChartTooltipV2 headers={[`${formattedLabel} – Forecasted`]} items={items} />;
    };

    const isForecasted = !lessThanIgnoreTime(payload[0]?.payload.date, earliestPredictedDate);

    return isForecasted ? forecastedRenderer() : historicalRenderer();
  };
};

export const buildSpendPrimaryOnlyTooltipRenderer = ({
  earliestPredictedDate,
  dateGrouping,
  fiscalCal,
  primaryBudget,
}: PrimaryOnlyTooltipBuilderInput): TooltipRenderer => {
  return ({ timestamp, payload }: TooltipInput) => {
    if (!payload || payload.length === 0) {
      return null;
    }

    const dateString = parseEpochDate(timestamp);
    const formattedLabel = headerFormatter(dateGrouping, dateString, fiscalCal);
    const dataPayload = payload[0].payload;

    const historicalRenderer = () => {
      const spendValue = dataPayload[`${HISTORICAL_KEY}-spend`];
      const cpmValue = dataPayload[`${HISTORICAL_KEY}-cpm`];

      const items: PayloadItem[] = [
        {
          label: "Spend",
          value: formatSpend(spendValue),
          color: Brand25,
          keyShape: KeyShapeOptions.SQUARE,
        },
        {
          label: "CPM",
          value: formatCpm(cpmValue),
          color: Neutral400,
          keyShape: KeyShapeOptions.LINE,
        },
      ];
      return <ChartTooltipV2 headers={[formattedLabel]} items={items} />;
    };

    const forecastedRenderer = () => {
      const spendValue = dataPayload[`${primaryBudget}-spend`];
      const cpmValue = dataPayload[`${primaryBudget}-cpm`];

      const items: PayloadItem[] = [
        {
          label: "Spend",
          value: formatSpend(spendValue),
          color: `url(#shadedStripeNeg${Brand50})`,
          keyShape: KeyShapeOptions.FILLED_RECT,
        },
        {
          label: "CPM",
          value: formatCpm(cpmValue),
          color: Brand100,
          keyShape: KeyShapeOptions.DASHED_LINE,
        },
      ];

      return <ChartTooltipV2 headers={[`${formattedLabel} – Forecasted`]} items={items} />;
    };

    const isForecasted = !lessThanIgnoreTime(payload[0]?.payload.date, earliestPredictedDate);

    return isForecasted ? forecastedRenderer() : historicalRenderer();
  };
};

interface ComparisonTooltipBuilderInput {
  earliestPredictedDate: BpmDateString;
  dateGrouping: string;
  fiscalCal: CompanyFiscalCal;
  selectedBudget: number | undefined;
  visibleBudgets: number[];
  colorMap: Record<string, string>;
  outcomeVar: OutcomeVariableType;
  kpiView: ForecastKpiViewOption;
}

export const buildKpiComparisonTooltipRenderer = ({
  earliestPredictedDate,
  dateGrouping,
  fiscalCal,
  selectedBudget,
  visibleBudgets,
  colorMap,
  outcomeVar,
  kpiView,
}: ComparisonTooltipBuilderInput): TooltipRenderer => {
  return ({ timestamp, payload }: TooltipInput) => {
    if (!payload || payload.length === 0) {
      return null;
    }

    const dateString = parseEpochDate(timestamp);
    const formattedLabel = headerFormatter(dateGrouping, dateString, fiscalCal);
    const dataPayload = payload[0].payload;
    const kpiViewSuffix = KPI_VIEW_SUFFICES[kpiView];
    const kpiCIViewSuffix = KPI_CI_VIEW_SUFFICES[kpiView];
    const label = ForecastChartViewLabels.Volume[kpiView];
    const formatKpi = getKpiFormatter(outcomeVar, kpiView);
    const formatCi = getCiFormatter(outcomeVar, kpiView);

    const historicalRenderer = () => {
      const kpiValue = dataPayload[`${HISTORICAL_KEY}-${kpiViewSuffix}`];

      const items: PayloadItem[] = [
        {
          label: `${label}, Historical`,
          value: formatKpi(kpiValue),
          color: Brand25,
          keyShape: KeyShapeOptions.SQUARE,
        },
      ];
      return <ChartTooltipV2 headers={[formattedLabel]} items={items} />;
    };

    const forecastedDefaultRenderer = () => {
      const items: PayloadItem[] = visibleBudgets.map(budget => {
        const kpiValue = dataPayload[`${budget}-${kpiViewSuffix}`];

        return {
          label: formatRawBudget(budget),
          value: formatKpi(kpiValue),
          color: colorMap[budget],
          keyShape: KeyShapeOptions.DASHED_LINE,
        };
      });

      return (
        <ChartTooltipV2
          headers={[formattedLabel]}
          items={items}
          subHeaderLabel="Budget"
          subHeaderValue={label}
        />
      );
    };

    const forecastedSelectedRenderer = () => {
      const budget = selectedBudget as number;
      const kpiValue = dataPayload[`${budget}-${kpiViewSuffix}`];
      const ciValue = dataPayload[`${budget}-${kpiCIViewSuffix}`];

      const items: PayloadItem[] = [
        {
          label,
          value: formatKpi(kpiValue),
          color: colorMap[budget],
          keyShape: KeyShapeOptions.DASHED_LINE,
        },
        {
          label: `${label} Range`,
          value: formatCi(ciValue),
          color: colorMap[budget],
          keyShape: KeyShapeOptions.SQUARE,
          keyOpacity: 0.25,
        },
      ];

      return <ChartTooltipV2 headers={[formattedLabel]} items={items} />;
    };

    const isForecasted = !lessThanIgnoreTime(payload[0]?.payload.date, earliestPredictedDate);

    if (!isForecasted) {
      return historicalRenderer();
    } else if (selectedBudget) {
      return forecastedSelectedRenderer();
    } else {
      return forecastedDefaultRenderer();
    }
  };
};

export const buildSpendComparisonTooltipRenderer = ({
  earliestPredictedDate,
  dateGrouping,
  fiscalCal,
  selectedBudget,
  visibleBudgets,
  colorMap,
}: ComparisonTooltipBuilderInput): TooltipRenderer => {
  return ({ timestamp, payload }: TooltipInput) => {
    if (!payload || payload.length === 0) {
      return null;
    }

    const dateString = parseEpochDate(timestamp);
    const formattedLabel = headerFormatter(dateGrouping, dateString, fiscalCal);
    const dataPayload = payload[0].payload;

    const historicalRenderer = () => {
      const spendValue = dataPayload[`${HISTORICAL_KEY}-spend`];

      const items: PayloadItem[] = [
        {
          label: "Spend, Historical",
          value: formatSpend(spendValue),
          color: Brand25,
          keyShape: KeyShapeOptions.SQUARE,
        },
      ];
      return <ChartTooltipV2 headers={[formattedLabel]} items={items} />;
    };

    const forecastedDefaultRenderer = () => {
      const items: PayloadItem[] = visibleBudgets.map(budget => {
        const spendValue = dataPayload[`${budget}-spend`];

        return {
          label: budget,
          value: formatSpend(spendValue),
          color: colorMap[budget],
          keyShape: KeyShapeOptions.DASHED_LINE,
        };
      });

      return (
        <ChartTooltipV2
          headers={[formattedLabel]}
          items={items}
          subHeaderLabel="Budget"
          subHeaderValue="Spend"
        />
      );
    };

    const forecastedSelectedRenderer = () => {
      const budget = selectedBudget as number;
      const spendValue = dataPayload[`${budget}-spend`];

      const items: PayloadItem[] = [
        {
          label: "Spend",
          value: formatSpend(spendValue),
          color: colorMap[budget],
          keyShape: KeyShapeOptions.DASHED_LINE,
        },
      ];

      return <ChartTooltipV2 headers={[formattedLabel]} items={items} />;
    };

    const isForecasted = !lessThanIgnoreTime(payload[0]?.payload.date, earliestPredictedDate);

    if (!isForecasted) {
      return historicalRenderer();
    } else if (selectedBudget) {
      return forecastedSelectedRenderer();
    } else {
      return forecastedDefaultRenderer();
    }
  };
};
